import { useState, useEffect } from 'react';
import { StandaloneAuth } from '@leagueplatform/auth-standalone';
import { decodeJwtPayload } from '../../utils/decode-jwt-payload';
import { captureMessageWithOwner } from '../../utils/sentry-helpers';
import { DISCOVERY_AUDIENCE } from '../../configs/discovery-config';

type DiscoveryTokenClaims = {
  'https://el/tenant_id'?: unknown;
  'https://el/accessible_tenants'?: unknown;
};

export type DiscoveryTokenClaimsResult = {
  tenantId: string | null;
  accessibleTenantIds: string[];
  isLoading: boolean;
  error: Error | null;
};

const MAX_RETRIES = 3;
const RETRY_DELAY_MS = 1000;

const getDiscoveryTenantId = (claimTenantId: unknown) =>
  typeof claimTenantId === 'string' && claimTenantId.trim().length > 0
    ? claimTenantId.trim()
    : null;

const getAccessibleTenantIds = (claimAccessibleTenantIds: unknown) => {
  if (claimAccessibleTenantIds === undefined) {
    return {
      accessibleTenantIds: [] as string[],
      errorMessage: 'Discovery token missing accessible_tenants claim',
      sentryMessage:
        'useDiscoveryTokenClaims: discovery token missing accessible_tenants claim',
      sentrySeverityLevel: 'error' as const,
      sentryContext: {
        discoveryToken: {},
      },
    };
  }

  if (!Array.isArray(claimAccessibleTenantIds)) {
    return {
      accessibleTenantIds: [] as string[],
      errorMessage: 'Discovery token has invalid accessible_tenants claim',
      sentryMessage:
        'useDiscoveryTokenClaims: discovery token has invalid accessible_tenants claim',
      sentrySeverityLevel: 'error' as const,
      sentryContext: {
        discoveryToken: {
          receivedClaimType: typeof claimAccessibleTenantIds,
          receivedClaimValue: claimAccessibleTenantIds,
        },
      },
    };
  }

  const accessibleTenantIds = claimAccessibleTenantIds
    .filter(
      (accessibleTenantId): accessibleTenantId is string =>
        typeof accessibleTenantId === 'string',
    )
    .map((accessibleTenantId) => accessibleTenantId.trim())
    .filter((accessibleTenantId) => accessibleTenantId.length > 0);

  if (accessibleTenantIds.length === 0) {
    return {
      accessibleTenantIds: [] as string[],
      errorMessage: 'Discovery token has no accessible tenants',
      sentryMessage:
        'useDiscoveryTokenClaims: discovery token has no accessible tenants',
      sentrySeverityLevel: 'error' as const,
      sentryContext: {
        discoveryToken: {
          receivedClaimType: 'array',
          receivedClaimValue: claimAccessibleTenantIds,
        },
      },
    };
  }

  if (accessibleTenantIds.length !== claimAccessibleTenantIds.length) {
    captureMessageWithOwner(
      'useDiscoveryTokenClaims: filtered invalid accessible_tenants entries from discovery token',
      {
        severityLevel: 'warning',
        context: {
          discoveryToken: {
            receivedClaimValue: claimAccessibleTenantIds,
            accessibleTenantIds,
          },
        },
      },
    );
  }

  return {
    accessibleTenantIds,
    errorMessage: null,
  };
};

/**
 * Attempts `getTokenSilently` with retries. After the Auth0 redirect callback,
 * the SDK may not have finished caching the token yet, causing an "External
 * interaction required" error on the first attempt. A brief retry handles this
 * race condition.
 *
 * Accepts an `isCancelled` callback so that in-progress retries abort when
 * the hook is disabled or the component unmounts, avoiding stale token
 * requests and timers after cleanup.
 */
const fetchTokenWithRetry = async (
  retriesLeft: number = MAX_RETRIES,
  isCancelled: () => boolean = () => false,
): Promise<string> => {
  try {
    return await StandaloneAuth.client.getTokenSilently({
      audience: DISCOVERY_AUDIENCE,
    });
  } catch (err) {
    if (retriesLeft > 0 && !isCancelled()) {
      await new Promise<void>((resolve) => {
        setTimeout(resolve, RETRY_DELAY_MS);
      });
      if (isCancelled()) throw err;
      return fetchTokenWithRetry(retriesLeft - 1, isCancelled);
    }
    throw err;
  }
};

/**
 * Fetches the Auth0 multi-tenant discovery token and decodes its JWT payload
 * to extract the `tenant_id` claim — the tenant the user selected during
 * the Auth0 domain discovery login flow.
 *
 * Uses `fetchTokenWithRetry` because this runs immediately after the Auth0
 * redirect callback, and the SDK may not have finished caching the token yet,
 * causing a transient "External interaction required" error.
 *
 * This is used by `RootHandler` to determine which tenant to navigate to
 * after authentication. Only runs when `enabled` is `true`.
 */
export const useDiscoveryTokenClaims = (
  enabled: boolean,
): DiscoveryTokenClaimsResult => {
  const [tenantId, setTenantId] = useState<string | null>(null);
  const [accessibleTenantIds, setAccessibleTenantIds] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(enabled);
  const [hasFetchedDiscoveryClaims, setHasFetchedDiscoveryClaims] =
    useState(false);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!enabled) {
      setTenantId(null);
      setAccessibleTenantIds([]);
      setError(null);
      setIsLoading(false);
      setHasFetchedDiscoveryClaims(false);
      return () => {};
    }

    let cancelled = false;

    setIsLoading(true);
    setHasFetchedDiscoveryClaims(false);
    setError(null);

    fetchTokenWithRetry(MAX_RETRIES, () => cancelled)
      .then((token) => {
        if (cancelled) return;

        const claims = decodeJwtPayload<DiscoveryTokenClaims>(token);
        const claimTenantId = getDiscoveryTenantId(
          claims['https://el/tenant_id'],
        );
        const accessibleTenantIdsResult = getAccessibleTenantIds(
          claims['https://el/accessible_tenants'],
        );

        if (accessibleTenantIdsResult.errorMessage) {
          const missingClaimError = new Error(
            accessibleTenantIdsResult.errorMessage,
          );
          captureMessageWithOwner(accessibleTenantIdsResult.sentryMessage, {
            severityLevel: accessibleTenantIdsResult.sentrySeverityLevel,
            context: {
              ...accessibleTenantIdsResult.sentryContext,
              discoveryToken: {
                ...accessibleTenantIdsResult.sentryContext.discoveryToken,
                errorMessage: missingClaimError.message,
              },
            },
          });
          setError(missingClaimError);
          setTenantId(null);
          setAccessibleTenantIds([]);
          return;
        }

        if (
          claimTenantId &&
          !accessibleTenantIdsResult.accessibleTenantIds.includes(claimTenantId)
        ) {
          captureMessageWithOwner(
            'useDiscoveryTokenClaims: tenant_id not present in accessible_tenants claim',
            {
              severityLevel: 'warning',
              context: {
                discoveryToken: {
                  tenantId: claimTenantId,
                  accessibleTenantIds:
                    accessibleTenantIdsResult.accessibleTenantIds,
                },
              },
            },
          );
        }

        setTenantId(claimTenantId);
        setAccessibleTenantIds(accessibleTenantIdsResult.accessibleTenantIds);
      })
      .catch((err) => {
        if (cancelled) return;

        const fetchError =
          err instanceof Error
            ? err
            : new Error('Failed to fetch discovery token.');

        captureMessageWithOwner(
          `useDiscoveryTokenClaims: discovery token fetch failed after ${MAX_RETRIES} retries`,
          {
            severityLevel: 'error',
            context: {
              discoveryToken: { errorMessage: fetchError.message },
            },
          },
        );

        setError(fetchError);
        setTenantId(null);
        setAccessibleTenantIds([]);
      })
      .finally(() => {
        if (cancelled) return;
        setIsLoading(false);
        setHasFetchedDiscoveryClaims(true);
      });

    return () => {
      cancelled = true;
    };
  }, [enabled]);

  return {
    tenantId,
    accessibleTenantIds,
    isLoading: isLoading || (enabled && !hasFetchedDiscoveryClaims),
    error,
  };
};
